import { useEffect, useState } from "react";
import { Send, X, Eye, Edit2 } from "lucide-react";
import DOMPurify from "dompurify";
import api, { formatErr } from "../lib/apiClient";

export default function SendEmailDialog({ entityType, entity, customer, onSent, onClose }) {
  const [to, setTo] = useState(customer?.email || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attach, setAttach] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("edit"); // edit | preview
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Item 10: fetch the rendered template so the user can preview before sending
  useEffect(() => {
    setPreviewLoading(true);
    api.get(`/${entityType}s/${entity.id}/email-preview`)
      .then(({ data }) => setPreview(data))
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [entityType, entity.id]);

  const send = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const payload = { to, attach_pdf: attach };
      if (subject) payload.subject = subject;
      if (body) payload.body_html = body;
      const { data } = await api.post(`/${entityType}s/${entity.id}/send`, payload);
      onSent?.(data);
      onClose();
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const effSubject = subject || preview?.subject || "";
  const effBody = body || preview?.body_html || "";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <form onSubmit={send} data-testid="send-email-dialog" className="bg-white dark:bg-slate-900 rounded-md w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100">
          <h3 className="font-display text-xl font-bold">Email {entityType} {entity.number}</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"><X size={18} /></button>
        </div>

        <div className="px-6 pt-3 flex gap-1 border-b border-slate-100">
          <button type="button" data-testid="email-mode-edit" onClick={() => setMode("edit")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${mode === "edit" ? "border-[#0F4C81] text-[#0F4C81]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <Edit2 size={13}/> Edit
          </button>
          <button type="button" data-testid="email-mode-preview" onClick={() => setMode("preview")}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${mode === "preview" ? "border-[#0F4C81] text-[#0F4C81]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <Eye size={13}/> Preview
          </button>
        </div>

        <div className="px-6 py-4 space-y-3 overflow-y-auto flex-1">
          {err && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 px-3 py-2 rounded">{err}</div>}

          {mode === "edit" ? (
            <>
              <div>
                <label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">To *</label>
                <input data-testid="email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} required className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md focus:outline-none focus:border-[#0F4C81]" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Subject</label>
                <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={preview?.subject || "Uses default template if blank"} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md focus:outline-none focus:border-[#0F4C81]" />
              </div>
              <div>
                <label className="text-xs uppercase text-slate-700 dark:text-slate-300 tracking-wider">Body (HTML overrides template)</label>
                <textarea data-testid="email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} placeholder="Uses default template if blank — switch to Preview to see the rendered template" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 dark:bg-slate-800 rounded-md focus:outline-none focus:border-[#0F4C81] font-mono" />
              </div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={attach} onChange={(e) => setAttach(e.target.checked)} /> Attach PDF</label>
            </>
          ) : (
            <div data-testid="email-preview-pane" className="space-y-3">
              {previewLoading && <div className="text-sm text-slate-500 py-8 text-center">Rendering preview…</div>}
              {!previewLoading && preview && (
                <>
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
                    <div><span className="text-slate-500 mr-2">To:</span><span className="font-medium">{to || preview.to}</span></div>
                    <div><span className="text-slate-500 mr-2">Subject:</span><span className="font-medium">{effSubject}</span></div>
                    <div><span className="text-slate-500 mr-2">Template:</span><span className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-slate-200">{preview.template_key}</span></div>
                  </div>
                  <div
                    className="border border-slate-200 rounded p-4 bg-white max-h-[420px] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(effBody || "", { USE_PROFILES: { html: true } }) }}
                  />
                  <p className="text-[11px] text-slate-500">This is how the email will render in the customer's inbox. Variables like <code className="bg-slate-100 px-1 rounded">{`{{customer_name}}`}</code> are already substituted.</p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-3 border-t border-slate-100">
          <button type="submit" disabled={loading} data-testid="send-email-submit" className="px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2 disabled:opacity-60">
            <Send size={14} /> {loading ? "Sending..." : "Send Email"}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-sm rounded-md hover:bg-slate-50 dark:hover:bg-slate-800">Cancel</button>
        </div>
      </form>
    </div>
  );
}
